/**
 * Screenshot capture with auto-mask of PII-bearing elements.
 *
 * Usage:
 *   const blob = await captureScreenshot({ hideElement: widgetRef.current })
 *
 * Returns a PNG Blob (≤ ~2 MB at 0.85 quality). Throws on capture failure
 * so the caller can show a friendly fallback ("сделайте скриншот вручную").
 */

import html2canvas from 'html2canvas-pro'

import { PII_MASKED_SELECTORS, PII_MASK_FILL } from '../../utils/piiMaskedSelectors'

const SELECTOR = PII_MASKED_SELECTORS.join(', ')

function _collectPiiRects() {
    const rects = []
    if (typeof document === 'undefined') return rects
    let nodes
    try {
        nodes = document.querySelectorAll(SELECTOR)
    } catch {
        return rects
    }
    for (const node of nodes) {
        const r = node.getBoundingClientRect()
        if (r.width <= 0 || r.height <= 0) continue
        rects.push({ x: r.left, y: r.top, w: r.width, h: r.height })
    }
    return rects
}

/**
 * @param {Object} opts
 * @param {HTMLElement|null} opts.hideElement — element to hide for the duration of capture
 *                                              (typically the support widget itself).
 * @returns {Promise<{ blob: Blob, dataUrl: string, maskedSelectorCount: number, width: number, height: number }>}
 */
export async function captureScreenshot(opts = {}) {
    const { hideElement = null } = opts
    let originalVisibility = null
    if (hideElement) {
        originalVisibility = hideElement.style.visibility
        hideElement.style.visibility = 'hidden'
    }

    // Capture ONLY the visible viewport (what the user actually sees), not the
    // full scroll height of the document. Rendering document.body without a crop
    // makes html2canvas draw the entire document — and it mis-places
    // `position: fixed` elements (drawers, popups), stretching them over the full
    // document length so the screenshot looks nothing like the live UI.
    //
    // The recipe below crops the render to the current viewport rectangle:
    //   - width/height  → output canvas = viewport size
    //   - windowWidth/Height → simulate a window == viewport (fixed elements are
    //     laid out against this, so they land where the user sees them)
    //   - x/y + scrollX/scrollY → offset the render by the current scroll so the
    //     captured slice matches the on-screen viewport at any scroll position
    const vw = window.innerWidth
    const vh = window.innerHeight
    let canvas
    try {
        canvas = await html2canvas(document.body, {
            useCORS: true,
            allowTaint: false,
            backgroundColor: '#ffffff',
            scale: Math.min(window.devicePixelRatio || 1, 2),
            logging: false,
            width: vw,
            height: vh,
            windowWidth: vw,
            windowHeight: vh,
            x: window.scrollX,
            y: window.scrollY,
            scrollX: -window.scrollX,
            scrollY: -window.scrollY,
        })
    } finally {
        if (hideElement) hideElement.style.visibility = originalVisibility || ''
    }

    // Overlay PII masks AFTER capture so they're baked into the final image.
    const rects = _collectPiiRects()
    if (rects.length > 0) {
        const ctx = canvas.getContext('2d')
        const scale = canvas.width / (window.innerWidth || canvas.width)
        ctx.fillStyle = PII_MASK_FILL
        for (const r of rects) {
            ctx.fillRect(r.x * scale, r.y * scale, r.w * scale, r.h * scale)
        }
    }

    const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
            (b) => (b ? resolve(b) : reject(new Error('canvas.toBlob returned null'))),
            'image/png',
            0.85,
        )
    })

    return {
        blob,
        dataUrl: canvas.toDataURL('image/png', 0.85),
        maskedSelectorCount: rects.length,
        width: canvas.width,
        height: canvas.height,
    }
}

/**
 * Apply a circular blur stroke to a canvas at (x, y) with the given radius.
 * Used by BlurBrush for user-paint blur on the preview.
 */
export function paintBlurDot(canvas, x, y, radius = 24) {
    const ctx = canvas.getContext('2d')
    ctx.save()
    ctx.beginPath()
    ctx.arc(x, y, radius, 0, Math.PI * 2)
    ctx.fillStyle = PII_MASK_FILL
    ctx.fill()
    ctx.restore()
}
